import {
  ResourceExplorer,
  ResourceExplorerWidget,
} from "@/services/cloudchipr.api";
import { addBudgetLineIntoBillingExplorerChartData } from "@/components/pages/common/billing-explorer/utils/addBudgetLineIntoBillingExplorerChartData";
import { billingExplorerChartDataGenerator } from "@/components/pages/common/billing-explorer/utils/billingExplorerChartDataGenerator";
import { billingExplorerChartDataLabelsGenerator } from "@/components/pages/common/billing-explorer/utils/billingExplorerChartDataLabelsGenerator";
import { addForecastedCostIntoBillingExplorerChartData } from "@/components/pages/common/billing-explorer/utils/addForecastedCostIntoBillingExplorerChartData";
import { HighchartsChartDataType } from "@/storybook/c8r-highcharts/common/utils/types/common";
import { chartTypeToHighchartsChartType } from "@/store/resource-explorer/utils/helpers/chart/chartTypeToHighchartsChartType";
import { aggregateResourceExplorerPercentBasedChartData } from "@/store/resource-explorer/utils/helpers/chart/aggregateResourceExplorerPercentBasedChartData";

export const generateChartData = (
  view: ResourceExplorer,
  viewData: ResourceExplorerWidget,
  showBudgetLine: boolean,
  quarterStartMonth: number,
): HighchartsChartDataType | null => {
  if (!viewData?.data) {
    return null;
  }

  const dataPoint = view?.data?.data_point;
  const dates = view?.data?.dates || {};

  let labels = billingExplorerChartDataLabelsGenerator(
    viewData.data,
    dataPoint,
    quarterStartMonth,
  );

  if (!labels) {
    return null;
  }

  let data = billingExplorerChartDataGenerator(viewData.data, labels);
  const isPercentBase = view?.chart_settings?.chart_base_type === "percent";

  const needToAddForecast =
    view?.chart_settings?.display_forecast &&
    dataPoint &&
    view?.data?.axis_granularity === "date" &&
    !isPercentBase &&
    viewData?.forecasted_costs;

  if (
    needToAddForecast &&
    (dates?.label === "month_to_date" || dates?.label === "quarter_to_date")
  ) {
    const { chartDataWithForecast, labelsWithForecast } =
      addForecastedCostIntoBillingExplorerChartData(
        data,
        dates.label,
        dataPoint,
        labels,
        viewData.forecasted_costs,
        quarterStartMonth,
      );

    data = chartDataWithForecast ?? data;
    labels = labelsWithForecast ?? labels;
  }

  if (
    showBudgetLine &&
    view?.budgets &&
    !isPercentBase &&
    view?.chart_settings?.chart_type !== "pie"
  ) {
    const selectedBudgetId = view.chart_settings?.selected_budget_id;
    let budget = undefined;

    if (selectedBudgetId) {
      budget = view.budgets.find((b) => b.id === selectedBudgetId);
    }

    if (!budget && view.budgets.length > 0) {
      budget = view.budgets[0];
    }

    if (budget) {
      data = addBudgetLineIntoBillingExplorerChartData(
        data,
        budget,
        labels,
        view?.data?.axis_granularity,
        quarterStartMonth,
      );
    }
  }

  return isPercentBase
    ? aggregateResourceExplorerPercentBasedChartData(data)
    : data;
};

export const extractChartSettings = (view: ResourceExplorer) => {
  return {
    scale: view?.chart_settings?.scale ?? "linear",
    show_legends: view?.chart_settings?.show_legends !== false,
    show_data_labels: view?.chart_settings?.show_data_labels || false,
    cumulative_line: view?.chart_settings?.cumulative_line || false,
    chart_type: chartTypeToHighchartsChartType(
      view?.chart_settings?.chart_type ?? "stacked_column",
    ),
    chart_base_type: view?.chart_settings?.chart_base_type ?? "cost",
  };
};
